<template>
  <el-tiptap
    :extensions="extensions"
    :content="tipTapInitialContent"
    placeholder="Write something ..."
    output="json"
    class="tiptap-container"
    @onInit="handleInit"
    @onUpdate="handleUpdate"
    @onTransaction="handleTransaction"
    :editorProperties="{
      editorProps: {
        handleDoubleClick: handleDoubleClick,
      },
    }"
  />
</template>

<script>
import EventBus from '@/helpers/event-bus';
import AssetsService from '@/api/assets.service';
// import this package's styles
import 'element-tiptap/lib/index.css';

import {
  Doc,
  Text,
  FontType,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Link,
  Image,
  Blockquote,
  BulletList,
  OrderedList,
  ListItem,
  TextAlign,
  HardBreak,
  History,
  TrailingNode,
  FormatClear,
  TextColor,
  TextHighlight,
  SelectAll,
  CodeBlock,
  Code,
  Print,
  Preview,
  Fullscreen,
  Title,
} from 'element-tiptap';

import { Search } from 'tiptap-extensions';
import { splitToParagraphs, textReplacements } from '@/helpers/nlp';
import MoreCommands from '@/plugins/tiptapExtensions/MoreCommands';

// import Magic from '@/components/TextEditor/Extensions/index';

export default {
  name: 'Editor',
  props: ['project'],
  data() {
    let tipTapInitialContent;
    if (this.project.doc) {
      tipTapInitialContent = {
        ...this.project.doc,
        content: [{ type: 'title', content: [{ type: 'text', text: this.project.name }] }, ...this.project.doc.content],
      };
    } else {
      const textReplaced = textReplacements(this.project.transcript, this.$store.state.user.textReplacements || {});
      const paragraphs = splitToParagraphs(textReplaced);
      const htmlParagraphs = paragraphs.reduce((acc, p) => `${acc}<p>${p}</p>`, '');
      tipTapInitialContent = `<h1>${this.project.name}</h1>${htmlParagraphs}`;
    }
    return {
      tipTapInitialContent,
      extensions: [
        new Doc({ title: true }),
        new Text(),
        new Paragraph(),
        new TrailingNode({ bubble: true }), // keeps blank line at bottom for edit ease
        new HardBreak(),
        new FontType({ bubble: false, menubar: false }),
        new TextColor({ bubble: false, menubar: false }),
        new TextHighlight({ bubble: false, menubar: false }),
        new Heading({ level: 4, bubble: true }),
        new Blockquote(),
        new Bold({ bubble: true, menubar: false }),
        new Underline({ bubble: true, menubar: false }),
        new Italic({ bubble: true, menubar: false }),
        new Link({ bubble: true, menubar: false }),
        new TextAlign(),
        new BulletList({ bubble: false }),
        new OrderedList({ bubble: false }),
        new ListItem(),
        new CodeBlock({ bubble: false, menubar: false }),
        new Code({ bubble: false, menubar: false }),
        new Image({
          uploadRequest: AssetsService.uploadFile,
        }),
        new FormatClear(),
        new Search({
          disableRegex: false,
          caseSensitive: true,
        }),
        new SelectAll({ menubar: false }),
        new Print({ menubar: false }),
        new Preview({ menubar: false }),
        new Fullscreen(),
        new History(),
        new Title({ placeholder: 'Add Title' }),
        new MoreCommands(),
        // new Magic()
      ],
    };
  },

  methods: {
    handleInit(editor) {
      EventBus.$emit('editorInit', editor);
    },
    handleUpdate(view, { getHTML, getJSON, state, transaction }) {
      EventBus.$emit('editorUpdate', view, { getHTML, getJSON, state, transaction });
    },
    handleTransaction() {
      EventBus.$emit('editorTransaction');
    },
    handleDoubleClick(view, pos) {
      console.log('double click');
      EventBus.$emit('editorDoubleClick', view, pos);
    },
  },
};
</script>

<style lang="scss">
.tiptap-container {
  .el-tiptap-editor__content {
    text-rendering: optimizeLegibility;
    font-family: Lora;
    font-size: 16px;
    line-height: 28px;
    .find {
      background: rgba(47, 0, 255, 0.726);
      color: white;
    }

    img {
      margin-top: 1rem;
    }

    /* Justify and hyphenate all paragraphs */
    p {
      /* text-align: justify; */
      hyphens: auto;
      -webkit-hyphens: auto;
      -moz-hyphens: auto;
      margin-top: 1rem;
    }

    // /* Make images easier to work with */
    // img {
    //   max-width: 100%;
    //   display: block;
    // }

    /* Styles for inline code or code snippets */
    code,
    pre,
    kbd {
      font-family: Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace !important;
      font-size: 85%;
    }
    // pre {
    //   padding: 1rem 1.4rem;
    //   max-width: 100%;
    //   overflow: auto;
    //   border-radius: 4px;
    //   background: hsl(210, 28%, 93%);
    // }
    // pre code {
    //   font-size: 95%;
    //   position: relative;
    // }
    kbd {
      background: hsl(210, 5%, 100%);
      border: 1px solid hsl(210, 5%, 70%);
      border-radius: 2px;
      padding: 2px 4px;
      font-size: 75%;
    }

    /* Make table 100% width, add borders between rows */
    table {
      border-collapse: collapse;
      border-spacing: 0;
      width: 100%;
      max-width: 100%;
    }
    th,
    td {
      text-align: left;
      padding: 0.5rem;
    }
    td {
      border-bottom: 1px solid hsl(0, 0%, 85%);
    }
    thead th {
      border-bottom: 2px solid hsl(0, 0%, 70%);
    }
    tfoot th {
      border-top: 2px solid hsl(0, 0%, 70%);
    }
    /* Heading typography */
    h1:first-of-type {
      font-weight: 600;
      font-size: 2.5em;
    }
    h1 {
      font-size: 2.5rem;
      line-height: 3.25rem;
      margin-bottom: 1.625rem;
    }

    h2 {
      font-size: 1.7rem;
      line-height: 2rem;
      margin-top: 3rem;
    }

    h3 {
      font-size: 1.4rem;
      margin-top: 2.5rem;
    }

    h4 {
      font-size: 1.2rem;
      margin-top: 2rem;
    }

    h5 {
      font-size: 1rem;
      margin-top: 1.8rem;
    }

    h6 {
      font-size: 1rem;
      font-style: italic;
      font-weight: normal;
      margin-top: 2.5rem;
    }

    h3,
    h4,
    h5,
    h6 {
      line-height: 1.625rem;
    }

    h1 + h2 {
      margin-top: 1.625rem;
    }

    h2 + h3,
    h3 + h4,
    h4 + h5 {
      margin-top: 0.8rem;
    }

    h5 + h6 {
      margin-top: -0.8rem;
    }

    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-bottom: 0.8rem;
    }
  }
}
</style>
